<script setup>
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

translator.getLanguages();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  inactive: {
    type: Boolean,
    default: false,
  },
  short: {
    type: Boolean,
    default: false,
  },
  allowedLoyalty: {
    type: Array,
    default: () => [],
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['removeTicket', 'changeLoyalty']);

const isShowDropDownLoyalty = ref(false);
const allowedLoyalty = computed(() => {
  if (props.allowedLoyalty === null) return null;
  return [{ id: 0, categoryId: 0, name: 'Основной' }, ...props.allowedLoyalty];
});
const currentLoyalty = computed(() => {
  if (props.ticket.loyaltyCategoryId === null) return { id: 0, categoryId: 0, name: 'Основной' };
  return allowedLoyalty.value.find((item) => item.id === props.ticket.loyaltyCategoryId);
});

const sectorName = computed(() => (props.ticket.sector.includes('Сектор') ? props.ticket.sector : `${translator.t("main", "Sector")} ${props.ticket.sector}`));

function toggleDropDownLoyalty() {
  if (props.isHistory) {
    return;
  }

  isShowDropDownLoyalty.value = !isShowDropDownLoyalty.value;
}

const loyaltyBtn = ref(null);
function clickOutsideDropdown(e) {
  if (e.target === loyaltyBtn.value || e.target.parentElement === loyaltyBtn.value) {
    return;
  }

  isShowDropDownLoyalty.value = false;
}

function changeLoyalty(categoryLoyalty) {
  emits(
    'changeLoyalty',
    {
      ticket: props.ticket,
      categoryId: categoryLoyalty.id,
      priceId: categoryLoyalty.categoryId ?? categoryLoyalty.priceId,
    },
  );

  isShowDropDownLoyalty.value = false;
}
</script>

<template>
  <div class="i-ticket" :class="{ inactive, short }">
    <div class="i-ticket__content">
      <div class="i-ticket__content-top">
        <template v-if="ticket.sector">
          <div class="i-ticket__sector">{{ sectorName }}</div>
          <div class="i-ticket__place">
            {{ translator.t("main", "Row") }} {{ ticket.row }}, {{ translator.t("main", "place") }}
            {{ ticket.col }}
          </div>
        </template>
        <template v-else-if="ticket.place">
          {{ ticket.place }}
        </template>
        <template v-else>
          {{ ticket.type }}
        </template>
      </div>
      <div class="i-ticket__bottom">
        <div class="i-ticket__loyalty-wrapper">
          <div
            v-if="props.allowedLoyalty?.length"
            ref="loyaltyBtn"
            class="dropdown"
            :disabled="props.inactive"
            :class="{ active: isShowDropDownLoyalty, dropdown_inactive: isHistory }"
            @click="toggleDropDownLoyalty"
          >
            <div class="dropdown__title">{{ currentLoyalty.name }}</div>
            <svg class="dropdown__type-svg" height="18" width="18">
              <use
                v-if="currentLoyalty.id === 0"
                xlink:href="#main-preferential-category"
              />
              <use
                v-else-if="currentLoyalty.id === 1"
                xlink:href="#child-preferential-category"
              />
              <use
                v-else
                xlink:href="#other-preferential-category"
              />
            </svg>
            <svg
              v-if="!props.inactive && !isHistory"
              class="dropdown__icon-down"
              height="16"
              width="16"
            >
              <use xlink:href="#down" />
            </svg>
          </div>
          <div
            v-if="isShowDropDownLoyalty"
            v-on-click-outside="clickOutsideDropdown"
            class="dropdown__list"
          >
            <div
              v-for="categoryLoyalty in allowedLoyalty"
              :key="categoryLoyalty.id"
              class="dropdown__item"
              @click="changeLoyalty(categoryLoyalty)"
            >
              <input
                v-model="currentLoyalty.id"
                type="radio"
                :value="categoryLoyalty.id"
              >
              <svg height="18" width="18">
                <use
                  v-if="categoryLoyalty.id === 0"
                  xlink:href="#main-preferential-category"
                />
                <use
                  v-else-if="categoryLoyalty.id === 1"
                  xlink:href="#child-preferential-category"
                />
                <use
                  v-else
                  xlink:href="#other-preferential-category"
                />
              </svg>
              {{ categoryLoyalty.name }}
            </div>
          </div>
        </div>
        <div class="i-ticket__bottom-wrapper">
          <div class="i-ticket__place-price">{{ ticket.price }} ₽</div>
          <div v-if="isHistory && ticket.isRefunded" class="i-ticket__refunded">
            {{ translator.t("main", "Refunded") }}
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="!isHistory"
      class="i-ticket__remove-button reset-btn"
      type="button"
      title="Удалить"
      @click="$emit('removeTicket', props.ticket)"
    >
      <svg height="15" width="15">
        <use xlink:href="#close" />
      </svg>
    </button>
  </div>
  <slot>
    <div />
  </slot>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-ticket.scss";
</style>
